import { Component, OnInit, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AuthService,
    AuthenticationFacade,
    PARAM_RETURN_URL,
    VersionService,
} from '@navfleet/client/authentication/domain/data-access';
import { LoginComponent } from '@navfleet/client/authentication/ui';
import { APP_ROUTES_ROOT_PATH } from '@navfleet/client/shared/constants/app';
import { filterTruthy, toArraySignal, toBooleanSignal } from '@navfleet/client/shared/util/functions';
import { TenantFacade } from '@navfleet/client/tenant/domain/data-access';
import { Tenant } from '@navfleet/client/tenant/domain/models';
import { Observable, take } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Component({
    selector: 'fl-login-container',
    imports: [LoginComponent],
    templateUrl: './login.container.html',
    styleUrls: ['./login.container.scss'],
})
export class LoginContainer implements OnInit {
    public readonly authFacade = inject(AuthenticationFacade);
    private readonly tenantFacade = inject(TenantFacade);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly versionService = inject(VersionService);
    private readonly authService = inject(AuthService);

    private readonly returnUrl = signal(APP_ROUTES_ROOT_PATH);

    public tenants$: Observable<Tenant[]> = this.tenantFacade.tenants$.pipe(
        filter((t) => !!t.length),
        map((t) => t.toSorted((a, b) => a.name.localeCompare(b.name)))
    );

    public readonly version = toSignal(this.versionService.get().pipe(map((v) => v.version)));
    public readonly tenants = toArraySignal(this.tenants$);
    public readonly isAuthenticated = toSignal(this.authFacade.isAuthenticated$, { initialValue: null });
    public readonly isLoading = toBooleanSignal(this.authFacade.isLoading$);

    constructor() {
        this.tenantFacade.selectedTenant$.pipe(take(1), filterTruthy()).subscribe(() => this.router.navigate(['/']));

        effect(() => {
            // Skip if null to avoid redirect loop if login not attempted yet
            if (this.isAuthenticated() === false) {
                this.authService.redirectToExternalLogin();
            }
        });
    }

    ngOnInit(): void {
        const returnUrl = this.route.snapshot.queryParams[PARAM_RETURN_URL];
        if (returnUrl) {
            this.returnUrl.set(returnUrl);
        }
    }

    public onTenantLogin(tenant: Tenant): void {
        this.tenantFacade.updateSelectedTenant(tenant);
        this.authFacade.loginTenant(tenant.id, this.returnUrl());
    }
}
